// FAQsPage.js
import React from 'react';
import { Box, Typography, Container, Breadcrumbs, Link as MuiLink, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'; // For Accordion

const FAQsPage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Resources', path: '/resources' },
    { name: 'FAQs', path: '/resources/faqs' },
  ];

  const faqs = [
    {
      question: 'How do I register on the platform?',
      answer: 'To register, click on the "Sign Up" button in the top right corner. You will need to provide some basic information and verify your email address. For businesses, additional verification documents may be required.',
    },
    {
      question: 'What types of scrap materials can I trade?',
      answer: 'Our platform supports a wide range of ferrous and non-ferrous metals, including steel, aluminum, copper, brass, lead, and specialized scrap like e-waste. Please refer to our "Materials" section for a comprehensive list.',
    },
    {
      question: 'How is pricing determined?',
      answer: 'Pricing is primarily market-driven, influenced by global commodity prices, supply and demand, and material quality. We provide market insights to help you make informed decisions, but final prices are negotiated between buyers and sellers.',
    },
    {
      question: 'What are the logistics for shipping and delivery?',
      answer: 'Logistics arrangements are typically agreed upon between the buyer and seller. Our platform can connect you with verified logistics partners, or you can use your preferred service. Details will be finalized during the negotiation process.',
    },
    {
      question: 'Is my personal and trading data secure?',
      answer: 'Yes, we prioritize the security and privacy of your data. We use advanced encryption and security protocols to protect your information and transactions. Please refer to our Privacy Policy for more details.',
    },
    {
      question: 'How do disputes get resolved?',
      answer: 'We have a dedicated dispute resolution process. If a disagreement arises, both parties can submit a formal complaint, and our team will mediate to find a fair resolution based on our terms of service.',
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Container sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: { xs: 2, md: 4 }, px: { xs: 2, md: 0 } }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) => (
              crumb.path ? (
                <MuiLink component={RouterLink} to={crumb.path} key={index} underline="hover" color="text.secondary" variant="body2">
                  {crumb.name}
                </MuiLink>
              ) : (
                <Typography key={index} color="text.primary" variant="body2">{crumb.name}</Typography>
              )
            ))}
          </Breadcrumbs>
        </Box>

        <Typography variant="h3" align="center" fontWeight={700} sx={{ mb: { xs: 4, md: 6 }, color: 'text.primary' }}>
          Frequently Asked Questions
        </Typography>

        <Typography variant="body1" align="center" sx={{ mb: { xs: 4, md: 6 }, maxWidth: '800px', mx: 'auto', color: 'text.secondary' }}>
          Have questions? We've got answers. Browse our comprehensive list of frequently asked questions to quickly find the information you need about our platform and services.
        </Typography>

        <Box sx={{ mt: 6 }}>
          {faqs.map((faq, index) => (
            <Accordion
              key={index}
              sx={{
                mb: 2,
                borderRadius: 2, // Rounded corners for accordion
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)', // Subtle shadow
                '&:before': {
                  display: 'none', // Remove default border
                },
                '&.Mui-expanded': {
                  margin: '16px 0', // Adjust margin when expanded
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'primary.dark' }} />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
                sx={{
                  bgcolor: 'primary.lightest', // Light golden yellow background for summary
                  borderRadius: 'inherit', // Inherit border radius from parent
                  '&.Mui-expanded': {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                  },
                }}
              >
                <Typography variant="h6" fontWeight={600} sx={{ color: 'text.primary' }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'grey.100' }}>
                <Typography variant="body1" color="text.secondary">
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default FAQsPage;
